import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight, Quote, Star } from "lucide-react";
import { Button } from "@/components/ui/button";

const TestimonialsCarousel = ({ testimonials = [] }) => {
  const { i18n } = useTranslation();
  const lang = i18n.language || "fr";
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [direction, setDirection] = useState("right");
  const [isPaused, setIsPaused] = useState(false);

  // Auto-play
  useEffect(() => {
    if (isPaused || testimonials.length <= 1) return;
    
    const interval = setInterval(() => {
      goToNext();
    }, 5000);
    
    return () => clearInterval(interval);
  }, [currentIndex, isPaused, testimonials.length]);

  const goToNext = useCallback(() => {
    if (isAnimating) return;
    setDirection("right");
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
      setIsAnimating(false);
    }, 300);
  }, [isAnimating, testimonials.length]);

  const goToPrev = useCallback(() => {
    if (isAnimating) return;
    setDirection("left");
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
      setIsAnimating(false);
    }, 300);
  }, [isAnimating, testimonials.length]);

  const goToSlide = (index) => {
    if (isAnimating || index === currentIndex) return;
    setDirection(index > currentIndex ? "right" : "left");
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentIndex(index);
      setIsAnimating(false);
    }, 300);
  };

  if (!testimonials || testimonials.length === 0) {
    return null;
  }

  const getTextContent = (text) => {
    if (!text) return "";
    if (typeof text === "string") return text;
    return text[lang] || text.fr || text.en || "";
  };

  // Get visible testimonials (current + neighbors for preview)
  const getVisibleTestimonials = () => {
    const items = [];
    for (let i = -1; i <= 1; i++) {
      const index = (currentIndex + i + testimonials.length) % testimonials.length;
      items.push({ ...testimonials[index], position: i });
    }
    return items;
  };

  const visibleItems = getVisibleTestimonials();
  const currentTestimonial = testimonials[currentIndex];

  return (
    <div 
      className="relative w-full"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      data-testid="testimonials-carousel"
    >
      {/* Main Carousel Container */}
      <div className="relative overflow-hidden py-8">
        {/* Gradient Overlays */}
        <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-[#1a1a2e] to-transparent z-10 pointer-events-none" />
        <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-[#1a1a2e] to-transparent z-10 pointer-events-none" />

        {/* Cards Container */}
        <div className="flex items-center justify-center gap-6 px-4">
          {visibleItems.map((item, idx) => {
            const isCenter = item.position === 0;
            const isLeft = item.position === -1;
            const isRight = item.position === 1;
            
            return (
              <div
                key={`${item.id}-${item.position}`}
                className={`
                  relative transition-all duration-500 ease-out
                  ${isCenter 
                    ? 'w-full max-w-2xl z-20 scale-100 opacity-100' 
                    : 'w-80 z-10 scale-90 opacity-40 blur-[1px] hidden md:block'
                  }
                  ${isAnimating 
                    ? direction === 'right' 
                      ? '-translate-x-4' 
                      : 'translate-x-4' 
                    : ''
                  }
                `}
                onClick={() => !isCenter && goToSlide((currentIndex + item.position + testimonials.length) % testimonials.length)}
                style={{ cursor: isCenter ? 'default' : 'pointer' }}
              >
                <div className={`
                  bg-white/10 backdrop-blur-md rounded-3xl p-8 md:p-10 
                  border border-white/20 shadow-2xl
                  ${isCenter ? 'bg-gradient-to-br from-white/15 to-white/5' : ''}
                  transition-all duration-500
                `}>
                  {/* Quote Icon */}
                  <div className="absolute -top-4 left-8">
                    <div className="bg-primary rounded-full p-3 shadow-lg">
                      <Quote className="h-6 w-6 text-white" />
                    </div>
                  </div>

                  {/* Rating Stars */}
                  <div className="flex gap-1 mb-6 pt-4">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={`h-5 w-5 transition-all duration-300 ${
                          i < (item.rating || 5)
                            ? "text-yellow-400 fill-yellow-400"
                            : "text-white/30"
                        }`}
                      />
                    ))}
                  </div>

                  {/* Testimonial Text */}
                  <p className={`
                    text-white leading-relaxed mb-8 italic
                    ${isCenter ? 'text-lg md:text-xl' : 'text-base line-clamp-4'}
                  `}>
                    "{getTextContent(item.text)}"
                  </p>

                  {/* Author Info */}
                  <div className="flex items-center gap-4">
                    {item.avatar_url ? (
                      <img
                        src={item.avatar_url}
                        alt={item.name}
                        className="w-14 h-14 rounded-full object-cover border-2 border-primary/50 shadow-lg"
                      />
                    ) : (
                      <div className="w-14 h-14 rounded-full bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center shadow-lg">
                        <span className="text-white text-xl font-bold">
                          {item.name?.charAt(0) || "?"}
                        </span>
                      </div>
                    )}
                    <div>
                      <p className="text-white font-semibold text-lg">{item.name}</p>
                      <p className="text-white/60 text-sm">
                        {typeof item.location === 'object' 
                          ? (item.location[lang] || item.location.fr || '') 
                          : item.location}
                      </p>
                      {(item.trip_type || item.circuit_type) && (
                        <p className="text-primary text-xs mt-1 font-medium">
                          {item.circuit_type === "4x4" || (item.trip_type?.fr || '').includes('4x4') 
                            ? "Circuit 4x4" 
                            : item.circuit_type === "trekking" || (item.trip_type?.fr || '').toLowerCase().includes('trek')
                            ? "Trek" 
                            : "Stage"}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Navigation Arrows */}
      <div className="absolute top-1/2 -translate-y-1/2 left-4 z-30">
        <Button
          variant="ghost"
          size="icon"
          onClick={goToPrev}
          className="h-12 w-12 rounded-full bg-white/10 hover:bg-white/20 text-white backdrop-blur-sm border border-white/20 shadow-lg transition-all hover:scale-110"
          data-testid="carousel-prev-btn"
        >
          <ChevronLeft className="h-6 w-6" />
        </Button>
      </div>
      <div className="absolute top-1/2 -translate-y-1/2 right-4 z-30">
        <Button
          variant="ghost"
          size="icon"
          onClick={goToNext}
          className="h-12 w-12 rounded-full bg-white/10 hover:bg-white/20 text-white backdrop-blur-sm border border-white/20 shadow-lg transition-all hover:scale-110"
          data-testid="carousel-next-btn"
        >
          <ChevronRight className="h-6 w-6" />
        </Button>
      </div>

      {/* Dots Navigation */}
      <div className="flex justify-center gap-2 mt-8">
        {testimonials.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`
              transition-all duration-300 rounded-full
              ${index === currentIndex 
                ? 'w-8 h-3 bg-primary' 
                : 'w-3 h-3 bg-white/30 hover:bg-white/50'
              }
            `}
            aria-label={`Go to slide ${index + 1}`}
            data-testid={`carousel-dot-${index}`}
          />
        ))}
      </div>

      {/* Progress Bar */}
      <div className="max-w-md mx-auto mt-4 h-1 bg-white/10 rounded-full overflow-hidden">
        <div 
          className="h-full bg-primary transition-all duration-300 ease-linear"
          style={{ 
            width: `${((currentIndex + 1) / testimonials.length) * 100}%`,
            transition: isPaused ? 'width 0.3s' : 'width 5s linear'
          }}
        />
      </div>

      {/* Counter */}
      <div className="text-center mt-4">
        <span className="text-white/60 text-sm">
          <span className="text-white font-semibold">{currentIndex + 1}</span>
          {" / "}
          {testimonials.length}
        </span>
      </div>
    </div>
  );
};

export default TestimonialsCarousel;
